import { Component, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { BentoAlertItemOptions } from '@bento/bento-ng';
import { Subscription } from 'rxjs';

interface ExportOption {
	type: string;
	name: string;
	value: boolean;
}

interface ImportResult {
	ErrorBundles: ErrorBundles[];
}

interface ErrorBundles {
	DtoType: string;
	ErrorDtos: ErrorDto[];
}

interface ErrorDto {
	error: {
		message: string;
	};
}

export class IdMapping {
	mainKey: string;
	mainValue: any[];
}

@Component({
	selector: 'app-import-export-test',
	templateUrl: './import-export-test.component.html',
	styleUrls: ['./import-export-test.component.css']
})
export class ImportExportTestComponent implements OnDestroy {
	public exportOptions: ExportOption[] = [
		{ type: 'accountReceivables', name: 'Accounts Receivables', value: false },
		{ type: 'activities', name: 'Activities', value: false },
		{ type: 'activityCategories', name: 'Activity Categories', value: false },
		{ type: 'bankAccounts', name: 'Bank Accounts', value: false },
		{ type: 'clients', name: 'Clients', value: false },
		{ type: 'clientBillings', name: 'Client Billings', value: false },
		{ type: 'clientEngagements', name: 'Client Engagements', value: false },
		{ type: 'clientMains', name: 'Client Mains', value: false },
		{ type: 'companyLayoutOptions', name: 'Company Layout Options', value: false },
		{ type: 'companyMains', name: 'Company Mains', value: false },
		{ type: 'contacts', name: 'Contacts', value: false },
		{ type: 'contactDataV1', name: 'Contact Data V1', value: false },
		{ type: 'contactData', name: 'Contact Data', value: false },
		{ type: 'contactImages', name: 'Contact Images', value: false },
		{ type: 'contactRelationships', name: 'Contact Relationships', value: false },
		{ type: 'departments', name: 'Departments', value: false },
		{ type: 'documentServicesFolders', name: 'Document Services Folders', value: false },
		{ type: 'documentServicesProjects', name: 'Document Services Projects', value: false },
		{ type: 'employees', name: 'Employees', value: false },
		{ type: 'employeeCostRates', name: 'Employee Cost Rates', value: false },
		{ type: 'employeeLevels', name: 'Employee Levels', value: false },
		{ type: 'employeeMains', name: 'Employee Mains', value: false },
		{ type: 'employeeRateDescriptions', name: 'Employee Rate Descriptions', value: false },
		{ type: 'engagementCategories', name: 'Engagement Categories', value: false },
		{ type: 'engagementTemplates', name: 'Engagement Templates', value: false },
		{ type: 'fixedFeeBillingTemplates', name: 'Fixed Fee Billing Templates', value: false },
		{ type: 'indirectTaxServicesTaxItems', name: 'Tax Items', value: false },
		{ type: 'indirectTaxServicesTaxGroups', name: 'Tax Groups', value: false },
		{ type: 'lateFeeBillingTemplates', name: 'Late Fee Billing Templates', value: false },
		{ type: 'offices', name: 'Offices', value: false },
		{ type: 'nationalIdentities', name: 'National Identities', value: false },
		{ type: 'profileServicesSubjectGroups', name: 'Subject Groups', value: false },
		{ type: 'profileServicesSubjectGroupMembers', name: 'Subject Group Members', value: false },
		{ type: 'profileServicesTerms', name: 'Terms', value: false },
		{ type: 'scheduleItems', name: 'Schedule Items', value: false },
		{ type: 'standardTexts', name: 'Standard Texts', value: false },
		{ type: 'taskTemplates', name: 'Task Templates', value: false },
		{ type: 'timeAndExpenses', name: 'Time and Expenses', value: false },
	];
	public selectAll = false;
	public modalLoading = false;
	public selectedFile: File[];
	public alerts: BentoAlertItemOptions[] = [];
	public error: any;
	public importResult: ImportResult;
	public idMappingsList = [];

	public modalInstance: NgbModalRef;

	public subs: Subscription[] = [];

	constructor(
		public modalService: NgbModal,
		public http: HttpClient
	) { }

	ngOnDestroy() {
		this.subs.forEach(sub => {
			sub.unsubscribe();
		});
	}

	public async launchModal(content: any) {
		this.modalInstance = this.modalService.open(content, { container: '.ng-bento' });
		await this.modalInstance.result.catch(error => {
			this.selectedFile = [];
			this.modalLoading = false;

			if (error !== 'close') {
				this.alerts.push({
					closeable: true,
					msg: error,
					type: 'danger',
					timeout: 5000
				});
			}
		});
		this.error = null;
		this.selectedFile = [];
		this.modalLoading = false;
	}

	public toggleAll() {
		this.exportOptions.forEach(option => {
			option.value = this.selectAll;
		});
	}

	public exportClose() {
		this.modalLoading = true;

		const postBody = {};
		this.exportOptions.forEach(option => {
			postBody[option.type] = {
				export: option.value
			};
		});
		this.subs.push(this.http.post('/api/data/v2/export', postBody, {responseType: 'text'}).subscribe(result => {
			this.modalLoading = false;
			this.modalInstance.close('export');

			/** Taken from
			 * https://stackoverflow.com/questions/38462894/how-to-create-and-save-file-to-local-filesystem-using-angularjs
			 */
			const file = new Blob([result], {
				type: 'text/plain'
			});
			const date = new Date();
			const fileURL = URL.createObjectURL(file);
			const a = document.createElement('a');
			a.href = fileURL;
			a.target = '_blank';
			a.download = `export-${date.toISOString()}.ndjson`;
			document.body.appendChild(a);
			a.click();
		}, error => {
			this.error = error;
			this.modalInstance.dismiss('Error Exporting Data');
		}));
	}


	public importFile() {
		this.modalLoading = true;
		this.importResult = null;
		const data = new FormData();
		data.append('file', this.selectedFile[0]);
		this.subs.push(this.http.post('/api/data/v2/import', data).subscribe((result: ImportResult) => {
			if (result) {
				this.importResult = result;
			}
			let msg = 'Data Successfully Imported';
			let type = 'success';

			if (this.importResult && this.importResult.ErrorBundles.length > 0) {
				msg = 'Data Imported With Errors';
				type = 'warning';
			}
			this.alerts.push({
				msg,
				type,
				closeable: true,
				timeout: 5000
			});

			this.modalInstance.close('Data Successfully Imported');
		}, error => {
			if (error.error) {
				this.error = error.error;
			}
			this.modalInstance.dismiss('Error Importing Data From File');
		}));
	}

	ConvertToCSV(excelList) {
		const array = typeof excelList !== 'object' ? JSON.parse(excelList) : excelList;
		let str = '';
		let row = '';
		for (const index of Object.keys(excelList[0])) {
			// Now convert each value to string and comma-separated
			row += index + ',';
		}

		row = row.slice(0, -1);
		// append Label row with line break
		str += row + '\r\n';

		for (const item of array) {
			let line = '';
			for (const index of Object.keys(item)) {
				if (line !== '') {
					line += ',' ;
					line += item[index];
				}
			}
			str += line + '\r\n';
		}
		return str;
	}

	public getIdMapping() {
		this.modalLoading = true;
		this.http.get<Map<string, Map< string, string >>>('/api/data/v2/import/id-mappings').subscribe((data) => {
			Object.keys(data).map(( key: any ) => {
				const idMapping = new IdMapping();
				idMapping.mainValue = [ ];
				Object.keys(data[key]).map(( keyChild: any ) => {
					idMapping.mainValue.push({ key: keyChild, value: data[key][keyChild] });
				});
				idMapping.mainKey = key;
				this.idMappingsList.push(idMapping);
			});
			const excelList = [];
			for ( const parentItem of this.idMappingsList) {
				if (parentItem.mainValue && parentItem.mainValue.length > 0 ) {
					for ( const mainValue of parentItem.mainValue ) {
						excelList.push({ 'Entity Name': parentItem.mainKey, 'Previous Id': mainValue.key, 'Current Id': mainValue.value });
					}
				} else {
					excelList.push({ 'Entity Name': parentItem.key, 'Previous Id': '', 'Current Id': ''});
				}
			}
			const csvData = this.ConvertToCSV(excelList);
			const a = document.createElement('a');
			a.setAttribute('style', 'display:none;');
			document.body.appendChild(a);
			const blob = new Blob([csvData], { type: 'text/csv' });
			const url = window.URL.createObjectURL(blob);
			a.href = url;
			a.download = 'id_mappings_list.csv';
			a.click();
			this.idMappingsList = [];
			return 'success';

		}, () => {
			this.modalInstance.dismiss('Error in loading data');
		});
	}
}
